import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Reveal } from "./primitives.jsx";
import { useSiteContent } from "../content.js";

// Greg wore №4 — so the section reads as a vintage broadcast on "Channel 4."
const CHANNEL = "04";

// The main tape — Greg's own career highlight reel. Self-hosted (compressed to
// ~24 MB H.264 with a faststart moov atom, under Cloudflare's 25 MiB static-asset
// cap) so it plays inline without leaving the site. The poster is the 1985 World
// Series Game 1 cap-tip, grabbed at 3:16 with the "4-GREG PRYOR" name graphic
// still in frame.
const FEATURED = {
  kind: "video",
  poster: "/assets/greg-highlights.jpg",
  sources: [{ src: "/assets/greg-highlights.mp4", type: "video/mp4" }],
  tag: "HIGHLIGHT REEL",
  matchup: "Career Highlights",
  team: "Chicago White Sox · Kansas City Royals",
  date: "1981 – 1985",
  network: "REEL",
  note: "A career in the infield, boiled down to a few minutes — White Sox at-bats, Royals blue, and a tip of the cap at the 1985 World Series.",
};

// The full WSNS broadcast — now one card in the on-deck row rather than a
// feature of its own, but still watchable: its card opens the lightbox. The
// thumbnail uses hqdefault.jpg — the only size that reliably exists for old
// uploads (maxres and sd both 404 here) — cover-cropped from 4:3 to fill the set.
const BROADCAST = {
  kind: "youtube",
  youtubeId: "MKxBC5A5y54",
  startSeconds: 19 * 60, // begin playback at 19:00
  poster: "/assets/game-tape-1980.jpg",
  tag: "FULL BROADCAST",
  matchup: "Royals at White Sox",
  team: "Chicago White Sox",
  date: "May 6, 1980",
  network: "WSNS",
  venue: "Comiskey Park",
  note: "A full original WSNS telecast from Comiskey Park — Greg in the infield for the White Sox, five seasons before the ring in Kansas City.",
};

// Join only the present meta parts so the highlight reel (which has no venue)
// doesn't render a dangling separator.
const metaLine = (m) => [m.date, m.venue, m.team].filter(Boolean).join(" · ");

function FeatureThumb({ src, alt }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return <div className="watch-static" aria-hidden />;
  }
  const webp = src.replace(/\.jpe?g$/i, ".webp");
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img
        className="watch-thumb"
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setErrored(true)}
      />
    </picture>
  );
}

export default function Watch() {
  const { watch: copy } = useSiteContent();
  // Which tape is playing in the lightbox: a media item (FEATURED | BROADCAST)
  // or null when closed. The modal renders a self-hosted <video> or a YouTube
  // iframe depending on the item's `kind`.
  const [active, setActive] = useState(null);
  const closeRef = useRef(null);

  // Match the memorabilia modal contract: Escape closes, body scroll locks
  // while open, and focus lands on the close button so keyboard users aren't
  // stranded behind the player.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const t = requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      cancelAnimationFrame(t);
    };
  }, [active]);

  return (
    <section id="watch" data-screen-label="Game Tape" className="section watch">
      <div className="section-inner">
        <div className="watch-head">
          <Reveal>
            <div className="watch-eyebrow mono">
              <span className="watch-rec-dot" aria-hidden />
              {copy.eyebrow}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="display h-xl watch-title">
              {copy.titlePrefix} <em>{copy.titleEmphasis}</em>
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="lede" style={{ maxWidth: "58ch" }}>
              {copy.lede}
            </p>
          </Reveal>
        </div>

        {/* Featured — the main tape: Greg's own highlight reel, self-hosted.
            Big, playable, opens the lightbox. */}
        <Reveal delay={120}>
          <button
            className="watch-feature"
            onClick={() => setActive(FEATURED)}
            aria-label={`Play the highlight reel: ${FEATURED.matchup}`}
          >
            <div className="watch-screen">
              <FeatureThumb src={FEATURED.poster} alt={`Greg Pryor — ${FEATURED.matchup}`} />
              <div className="watch-scanlines" aria-hidden />
              <div className="watch-screen-grad" aria-hidden />
              <div className="watch-chrome">
                <span className="watch-ch">CH {CHANNEL}</span>
                <span className="watch-rec">
                  <span className="watch-rec-dot" aria-hidden />
                  REC
                </span>
                <span className="watch-net">{FEATURED.network}</span>
              </div>
              <div className="watch-play" aria-hidden>
                <span className="watch-play-tri" />
              </div>
              <div className="watch-feature-meta">
                <span className="watch-tag mono">{FEATURED.tag}</span>
                <span className="watch-feature-title">{FEATURED.matchup}</span>
                <span className="watch-feature-sub mono">{metaLine(FEATURED)}</span>
              </div>
            </div>
          </button>
        </Reveal>

        {/* The second tape: the full WSNS broadcast. (Two "coming soon" cards
            for footage still being found sat next to it until Oct 2026; empty
            TV static was clutter. The note still says more is being dug up.) */}
        <div className="watch-ondeck-head">
          <span className="mono">ON DECK</span>
          <span className="watch-ondeck-rule" aria-hidden />
          <span className="mono watch-ondeck-note">{copy.ondeckNote}</span>
        </div>

        <div className="watch-grid">
          {/* Opens the lightbox, like the feature. */}
          <Reveal>
            <button
              className="watch-card watch-card-play"
              onClick={() => setActive(BROADCAST)}
              aria-label={`Play full broadcast: ${BROADCAST.matchup}, ${BROADCAST.date}`}
            >
              <div className="watch-screen">
                <FeatureThumb
                  src={BROADCAST.poster}
                  alt={`${BROADCAST.matchup}, ${BROADCAST.date}`}
                />
                <div className="watch-scanlines" aria-hidden />
                <div className="watch-chrome">
                  <span className="watch-ch">CH {CHANNEL}</span>
                  <span className="watch-card-tag mono">{BROADCAST.tag}</span>
                </div>
                <div className="watch-play" aria-hidden>
                  <span className="watch-play-tri" />
                </div>
              </div>
              <div className="watch-card-foot">
                <span className="watch-card-title">{BROADCAST.matchup}</span>
                <span className="watch-card-sub mono">
                  {BROADCAST.date} · {BROADCAST.venue}
                </span>
              </div>
            </button>
          </Reveal>
        </div>
      </div>

      {active &&
        createPortal(
          <div
            className="watch-modal"
            onClick={() => setActive(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`${active.matchup} — ${active.kind === "video" ? "highlight reel" : "full broadcast"}`}
          >
            <div className="watch-modal-card" onClick={(e) => e.stopPropagation()}>
              <button
                ref={closeRef}
                className="watch-modal-close"
                onClick={() => setActive(null)}
                aria-label="Close player"
              >
                ×
              </button>
              <div className="watch-modal-chrome">
                <span className="watch-ch">CH {CHANNEL}</span>
                <span className="watch-rec">
                  <span className="watch-rec-dot" aria-hidden />
                  ON AIR
                </span>
                <span className="watch-modal-chrome-meta mono">
                  {active.network} · {active.date}
                </span>
              </div>
              <div className="watch-modal-screen">
                {active.kind === "video" ? (
                  <video
                    className="watch-modal-video"
                    controls
                    autoPlay
                    playsInline
                    preload="auto"
                    poster={active.poster}
                  >
                    {active.sources.map((s) => (
                      <source key={s.src} src={s.src} type={s.type} />
                    ))}
                  </video>
                ) : (
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${active.youtubeId}?autoplay=1&rel=0&modestbranding=1&start=${active.startSeconds}`}
                    title={`${active.matchup} — ${active.date} full broadcast`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  />
                )}
              </div>
              <div className="watch-modal-foot">
                <div className="watch-modal-foot-text">
                  <span className="watch-modal-title">{active.matchup}</span>
                  <span className="watch-modal-note">{active.note}</span>
                </div>
                {active.kind === "youtube" && (
                  <a
                    className="watch-yt-link mono"
                    href={`https://youtu.be/${active.youtubeId}?t=${active.startSeconds}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch on YouTube ↗
                  </a>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}

const watchStyles = document.createElement("style");
watchStyles.textContent = `
.watch { background: var(--navy-900); }
.watch-head { margin-bottom: 44px; }
.watch-title { color: var(--bone); margin: 22px 0 30px; }
.watch-title em { color: var(--gold); font-style: italic; }

/* Eyebrow — broadcast "REC" cue instead of a section number */
.watch-eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 0.6875rem; font-weight: 500; letter-spacing: 0.2em;
  text-transform: uppercase; color: var(--gold);
}
.watch-rec-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--crimson);
  box-shadow: 0 0 10px rgba(200,16,46,0.8);
  animation: watchBlink 1.2s steps(1, end) infinite;
}
@keyframes watchBlink { 0%, 55% { opacity: 1; } 56%, 100% { opacity: 0.2; } }

/* The "TV set" — shared by the featured tape and the on-deck cards */
.watch-screen {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #060f1d;
  border: 1px solid var(--rule);
}
.watch-thumb {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  filter: saturate(0.82) contrast(1.06) sepia(0.12) brightness(0.94);
  transition: transform 700ms cubic-bezier(.2,.8,.2,1), filter 400ms;
}
/* Hero poster: lift the framing toward Greg's cap-tip so the title clears the
   in-frame "4-GREG PRYOR" broadcast lower-third. */
.watch-feature:not(.is-secondary) .watch-thumb { object-position: center 22%; }
.watch-scanlines {
  position: absolute; inset: 0; z-index: 2; pointer-events: none;
  background: repeating-linear-gradient(0deg, rgba(0,0,0,0.20) 0 1px, transparent 1px 3px);
  mix-blend-mode: multiply;
  opacity: 0.5;
}
.watch-screen-grad {
  position: absolute; inset: 0; z-index: 2; pointer-events: none;
  background:
    radial-gradient(120% 80% at 50% 38%, transparent 55%, rgba(5,9,18,0.5) 100%),
    linear-gradient(0deg, rgba(5,9,18,0.82) 0%, rgba(5,9,18,0.1) 42%, transparent 60%);
}

/* Channel bar across the top of each screen */
.watch-chrome {
  position: absolute; top: 0; left: 0; right: 0; z-index: 4;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  font-family: var(--mono); font-size: 0.625rem; letter-spacing: 0.16em;
}
.watch-ch {
  color: var(--gold);
  border: 1px solid var(--rule-strong);
  background: rgba(5,13,26,0.7);
  padding: 4px 8px;
}
.watch-rec {
  display: inline-flex; align-items: center; gap: 6px;
  color: var(--crimson); letter-spacing: 0.2em;
}
.watch-net { margin-left: auto; color: var(--bone-dim); opacity: 0.85; }

/* Play button */
.watch-play {
  position: absolute; z-index: 3;
  top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: 76px; height: 76px; border-radius: 50%;
  background: rgba(189,155,96,0.94);
  display: grid; place-items: center;
  box-shadow: 0 16px 40px -10px rgba(0,0,0,0.6), 0 0 0 1px rgba(244,241,234,0.15) inset;
  transition: transform 300ms cubic-bezier(.2,.8,.2,1), background 220ms, box-shadow 300ms;
}
.watch-play-tri {
  width: 0; height: 0;
  border-style: solid;
  border-width: 11px 0 11px 19px;
  border-color: transparent transparent transparent var(--navy-900);
  margin-left: 5px;
}

/* Featured tape */
.watch-feature {
  display: block; width: 100%; padding: 0;
  border: 1px solid var(--rule);
  background: #060f1d;
  cursor: pointer;
  font-family: inherit;
  transition: transform 300ms cubic-bezier(.2,.8,.2,1), border-color 300ms, box-shadow 300ms;
}
.watch-feature .watch-screen { border: none; }
.watch-feature:hover,
.watch-feature:focus-visible {
  transform: translateY(-3px);
  border-color: var(--royal-blue-bright);
  box-shadow: 0 36px 70px -24px rgba(0, 70, 173, 0.55);
  outline: none;
}
.watch-feature:focus-visible { box-shadow: 0 0 0 2px var(--royal-blue-glow), 0 36px 70px -24px rgba(0,70,173,0.55); }
.watch-feature:hover .watch-thumb { transform: scale(1.045); filter: saturate(0.95) contrast(1.06) brightness(1); }
.watch-feature:hover .watch-play { transform: translate(-50%, -50%) scale(1.08); background: var(--gold-bright); box-shadow: 0 20px 48px -10px rgba(0,0,0,0.7), 0 0 0 6px rgba(189,155,96,0.18); }

.watch-feature-meta {
  position: absolute; z-index: 4; left: 22px; bottom: 20px; right: 22px;
  display: flex; flex-direction: column; gap: 8px;
}
.watch-tag {
  align-self: flex-start;
  font-size: 0.5625rem; letter-spacing: 0.2em; color: var(--navy-900);
  background: var(--gold); padding: 4px 9px;
}
.watch-feature-title {
  font-family: var(--serif); font-weight: 500;
  font-size: clamp(1.375rem, 3.4vw, 2.375rem); line-height: 1.05;
  color: var(--bone); letter-spacing: -0.01em;
}
.watch-feature-sub { font-size: 0.6875rem; letter-spacing: 0.14em; color: var(--gold-bright); }

/* On-deck header */
.watch-ondeck-head {
  display: flex; align-items: center; gap: 16px;
  margin: 52px 0 20px;
  font-family: var(--mono); font-size: 0.6875rem; letter-spacing: 0.2em;
  color: var(--gold);
}
.watch-ondeck-rule { flex: 0 0 36px; height: 1px; background: var(--rule-strong); }
.watch-ondeck-note { color: var(--bone-dim); opacity: 0.7; letter-spacing: 0.14em; }

/* On-deck grid — one watchable tape + two teasers, three to a row. */
.watch-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.watch-card {
  border: 1px solid var(--rule);
  background: #060f1d;
  transition: border-color 300ms, transform 300ms cubic-bezier(.2,.8,.2,1);
}
.watch-card:hover { border-color: var(--rule-strong); transform: translateY(-2px); }
.watch-screen-soon { background: #060f1d; }

/* Tuned-out "snow" — reuses the site's grain noise, tinted + drifting */
.watch-static {
  position: absolute; inset: 0; z-index: 1;
  background-color: #0a1525;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxNjAnIGhlaWdodD0nMTYwJz48ZmlsdGVyIGlkPSduJz48ZmVUdXJidWxlbmNlIHR5cGU9J2ZyYWN0YWxOb2lzZScgYmFzZUZyZXF1ZW5jeT0nMC44NScgbnVtT2N0YXZlcz0nMicgc3RpdGNoVGlsZXM9J3N0aXRjaCcvPjxmZUNvbG9yTWF0cml4IHZhbHVlcz0nMCAwIDAgMCAwICAwIDAgMCAwIDAgIDAgMCAwIDAgMCAgMCAwIDAgMC40MiAwJy8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgZmlsdGVyPSd1cmwoI24pJy8+PC9zdmc+");
  background-size: 180px 180px;
  opacity: 0.5;
  animation: watchSnow 0.9s steps(4, end) infinite;
}
@keyframes watchSnow {
  0% { background-position: 0 0; }
  25% { background-position: -30px 14px; }
  50% { background-position: 22px -18px; }
  75% { background-position: -16px -8px; }
  100% { background-position: 12px 20px; }
}
.watch-soon-badge {
  margin-left: auto; color: var(--bone-dim);
  border: 1px solid var(--rule); padding: 4px 8px;
  background: rgba(5,13,26,0.6);
}
.watch-card-center {
  position: absolute; z-index: 3; inset: 0;
  display: grid; place-items: center;
  font-size: 0.8125rem; letter-spacing: 0.34em; color: var(--bone);
  text-shadow: 0 2px 12px rgba(0,0,0,0.7);
}
.watch-card-foot {
  display: flex; flex-direction: column; gap: 5px;
  padding: 14px 16px;
  border-top: 1px solid var(--rule);
  background: rgba(5, 13, 26, 0.6);
}
.watch-card-title {
  font-family: var(--serif); font-size: 1rem; font-weight: 500;
  color: var(--bone); line-height: 1.2;
}
.watch-card-sub { font-size: 0.625rem; letter-spacing: 0.12em; color: var(--gold); }

/* Playable on-deck card — the one watchable tape. Same footprint as the
   teasers, but it gets a real thumbnail, a play button, and lifts on hover. */
.watch-card-play {
  display: block; width: 100%; padding: 0;
  font-family: inherit; text-align: left; cursor: pointer;
}
.watch-card-play:hover,
.watch-card-play:focus-visible {
  border-color: var(--royal-blue-bright);
  transform: translateY(-2px);
  box-shadow: 0 24px 50px -20px rgba(0, 70, 173, 0.5);
  outline: none;
}
.watch-card-play:focus-visible {
  box-shadow: 0 0 0 2px var(--royal-blue-glow), 0 24px 50px -20px rgba(0, 70, 173, 0.5);
}
.watch-card-play:hover .watch-thumb {
  transform: scale(1.05);
  filter: saturate(0.95) contrast(1.06) brightness(1);
}
.watch-card-play .watch-play { width: 54px; height: 54px; }
.watch-card-play .watch-play-tri { border-width: 8px 0 8px 14px; }
.watch-card-play:hover .watch-play {
  transform: translate(-50%, -50%) scale(1.08);
  background: var(--gold-bright);
}
/* Gold "watchable" tag — mirrors the dim COMING SOON badge's slot. */
.watch-card-tag {
  margin-left: auto;
  font-size: 0.5625rem; letter-spacing: 0.2em;
  color: var(--navy-900); background: var(--gold);
  padding: 4px 8px;
}

@media (max-width: 900px) {
  .watch-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 460px) {
  .watch-grid { grid-template-columns: 1fr; gap: 12px; }
  .watch-feature-meta { left: 16px; right: 16px; bottom: 16px; }
  .watch-play { width: 60px; height: 60px; }
  .watch-play-tri { border-width: 9px 0 9px 15px; }
  .watch-ondeck-note { display: none; }
}

/* ---- Lightbox ---- */
.watch-modal {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(5, 13, 26, 0.9);
  backdrop-filter: blur(10px);
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
  animation: watchFadeIn 240ms;
}
@keyframes watchFadeIn { from { opacity: 0; } to { opacity: 1; } }
.watch-modal-card {
  position: relative;
  width: 100%; max-width: 960px;
  background: var(--navy-800);
  border: 1px solid var(--royal-blue-bright);
  box-shadow: 0 40px 100px -20px rgba(0,0,0,0.78), 0 0 0 1px rgba(0,70,173,0.4);
  animation: watchScaleIn 300ms cubic-bezier(.2,.8,.2,1);
  overflow: hidden;
}
@keyframes watchScaleIn { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: scale(1); } }
.watch-modal-close {
  position: absolute; top: 12px; right: 12px; z-index: 6;
  width: 38px; height: 38px; border-radius: 50%;
  background: rgba(5,13,26,0.85); border: 1px solid var(--rule);
  color: var(--bone); font-size: 1.375rem; line-height: 1; cursor: pointer;
  transition: background 200ms, border-color 200ms;
}
.watch-modal-close:hover { background: var(--royal-blue-bright); border-color: var(--royal-blue-bright); }
.watch-modal-chrome {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
  font-family: var(--mono); font-size: 0.625rem; letter-spacing: 0.16em;
}
.watch-modal-chrome-meta { margin-left: auto; color: var(--bone-dim); padding-right: 44px; }
.watch-modal-screen {
  position: relative; width: 100%; aspect-ratio: 16 / 9;
  background: #000;
}
.watch-modal-screen iframe {
  position: absolute; inset: 0; width: 100%; height: 100%; border: 0;
}
/* Self-hosted player — contain so the 720×438 source isn't stretched to the
   16:9 frame; the side pillarbox bars stay black. */
.watch-modal-video {
  position: absolute; inset: 0; width: 100%; height: 100%;
  border: 0; background: #000; object-fit: contain;
}
.watch-modal-foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 20px; flex-wrap: wrap;
  padding: 20px 22px;
  border-top: 1px solid var(--rule);
}
.watch-modal-foot-text { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.watch-modal-title { font-family: var(--serif); font-size: 1.25rem; font-weight: 500; color: var(--bone); }
.watch-modal-note { font-size: 0.8125rem; line-height: 1.55; color: var(--bone-dim); }
.watch-yt-link {
  flex: 0 0 auto;
  display: inline-flex; align-items: center;
  font-size: 0.6875rem; letter-spacing: 0.14em; color: var(--gold);
  border: 1px solid var(--rule-strong); padding: 10px 16px;
  white-space: nowrap;
  transition: background 200ms, color 200ms, border-color 200ms;
}
.watch-yt-link:hover { background: var(--gold); color: var(--navy-900); border-color: var(--gold); }

@media (max-width: 560px) {
  .watch-modal { padding: 14px; }
  .watch-modal-chrome { gap: 9px; font-size: 0.5625rem; padding: 12px; }
  .watch-modal-chrome .watch-ch,
  .watch-modal-chrome .watch-rec { white-space: nowrap; }
  .watch-modal-chrome-meta { white-space: nowrap; padding-right: 50px; }
  .watch-modal-foot { padding: 16px; gap: 14px; }
  .watch-modal-note { font-size: 0.75rem; }
  .watch-yt-link { width: 100%; justify-content: center; }
}
`;
document.head.appendChild(watchStyles);
